import React, { useState, useEffect } from 'react';
import { signatureFonts } from '@/data/signatureFonts';
import { cn } from '@/lib/utils';
import { PenLine, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  signatureText: string;
  selectedFont: string;
  onFontChange: (fontId: string) => void;
  onTextChange?: (text: string) => void;
}

// Format signature: First letter uppercase, rest lowercase for each word
const formatSignatureText = (text: string): string => {
  return text
    .split(' ')
    .map(word => {
      if (word.length === 0) return word;
      // Handle initials like "A." - keep as is
      if (word.length <= 2 && word.endsWith('.')) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      // Normal words: first letter uppercase, rest lowercase
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

export const SignatureModal: React.FC<SignatureModalProps> = ({
  isOpen,
  onClose,
  signatureText,
  selectedFont,
  onFontChange,
  onTextChange,
}) => {
  const [localText, setLocalText] = useState(signatureText);
  const [showSaved, setShowSaved] = useState(false);

  // Sync local text with prop
  useEffect(() => {
    setLocalText(signatureText);
  }, [signatureText]);

  const displayText = localText || 'Your Signature';

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatSignatureText(e.target.value);
    setLocalText(formatted);
    onTextChange?.(formatted);
  };

  const handleFontSelect = (fontId: string) => {
    onFontChange(fontId);
    // Show "Saved" indicator
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-primary" />
            Select Signature Style
          </DialogTitle>
        </DialogHeader>

        {/* Editable signature preview - narrower */}
        <div className="p-4 rounded-lg border border-border bg-muted/30 mb-4 relative">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-muted-foreground">Current Signature</p>
            {/* Saved indicator */}
            <div className={cn(
              "flex items-center gap-1 text-xs text-primary transition-all duration-300",
              showSaved ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"
            )}>
              <Check className="h-3 w-3" />
              <span>Saved</span>
            </div>
          </div>
          <input
            type="text"
            value={localText}
            onChange={handleTextChange}
            placeholder="Enter your signature..."
            className="w-full bg-transparent border-none text-5xl h-auto py-2 px-0 outline-none placeholder:text-muted-foreground"
            style={{ fontFamily: signatureFonts.find(f => f.id === selectedFont)?.fontFamily || 'cursive' }}
          />
        </div>

        {/* Font Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {signatureFonts.map((font) => (
            <button
              key={font.id}
              type="button"
              onClick={() => handleFontSelect(font.id)}
              className={cn(
                'relative p-4 rounded-lg border-2 transition-all duration-200 text-center',
                'hover:border-primary/50 hover:bg-accent/50',
                selectedFont === font.id
                  ? 'border-primary bg-primary/10 shadow-md'
                  : 'border-border bg-background'
              )}
            >
              <div
                className="text-xl leading-tight truncate text-foreground mb-1"
                style={{ fontFamily: font.fontFamily }}
              >
                {displayText}
              </div>
              <div className="text-[10px] text-muted-foreground font-sans">
                {font.displayName}
              </div>
              {selectedFont === font.id && (
                <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-primary" />
              )}
            </button>
          ))}
        </div>

        {/* Close button */}
        <div className="flex justify-end mt-4">
          <Button onClick={onClose}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};